/**
 * sunlight - Sonnenstand für die 3D-Szene (#604)
 *
 * Ab #604 echt: SunCalc liefert Azimut/Höhe aus Lat/Lon/Datum, hier in
 * Weltkoordinaten umgerechnet und via northAngle korrekt zum Garten
 * orientiert. Renderer (directionalLight + <Sky>) bleibt unverändert — er
 * bekommt nur eine bewegte Lichtquelle statt der früheren statischen
 * Mittagssonne (#599-Naht).
 *
 * Der Rechenkern ist bewusst WebGL-frei (reine Funktionen, Jest-testbar) und
 * wird ab #605 auch vom 2D-Schattenrechner konsumiert.
 *
 * Konventionen:
 * - northAngle: Grad im Uhrzeigersinn von Canvas-oben zu Norden
 *   (0 = Norden oben, 90 = Norden rechts); null → 0.
 * - Welt: x = Canvas-x, z = Canvas-y, y = Höhe. Canvas-oben entspricht -z.
 * - SunCalc: azimuth in Radiant, von Süden nach Westen positiv (Süd = 0,
 *   West = +π/2, Ost = -π/2); altitude in Radiant über dem Horizont.
 */

import SunCalc from 'suncalc';

const DEG_TO_RAD = Math.PI / 180;

/**
 * Sonnenstand für einen Ort und Zeitpunkt.
 * Dünner SunCalc-Wrapper — die Umrechnung in Weltkoordinaten macht
 * getSunDirection3D, damit dieser Teil ohne Bibliothek testbar bleibt.
 *
 * @param {number} lat - Breitengrad (Grad)
 * @param {number} lon - Längengrad (Grad)
 * @param {Date} date - Zeitpunkt
 * @returns {{ azimuth: number, altitude: number }} Radiant; altitude ≤ 0 = Sonne unter dem Horizont
 */
export function getSunPosition(lat, lon, date) {
  const { azimuth, altitude } = SunCalc.getPosition(date, lat, lon);
  return { azimuth, altitude };
}

/**
 * Einheitsvektor vom Garten zur Sonne, in Weltkoordinaten.
 *
 * Der geografische Kurs zur Sonne (von Norden im Uhrzeigersinn) ist
 * π + azimuth (SunCalc misst von Süden). Um northAngle in Plan-Koordinaten
 * gedreht ergibt sich der horizontale Anteil; altitude liefert die Höhe.
 *
 * @param {number} azimuth - SunCalc-Azimut (Radiant, von Süden nach Westen)
 * @param {number} altitude - SunCalc-Höhe (Radiant)
 * @param {number|null} northAngle - Grad im Uhrzeigersinn von Canvas-oben zu Norden
 * @returns {{x: number, y: number, z: number}} normalized direction
 */
export function getSunDirection3D(azimuth, altitude, northAngle) {
  const bearing = (northAngle ?? 0) * DEG_TO_RAD + Math.PI + azimuth;
  const cosAlt = Math.cos(altitude);
  return {
    x: Math.sin(bearing) * cosAlt,
    y: Math.sin(altitude),
    z: -Math.cos(bearing) * cosAlt,
  };
}

/**
 * Position einer directionalLight, als [x, y, z] im gegebenen Abstand.
 *
 * @param {number} azimuth - SunCalc-Azimut (Radiant)
 * @param {number} altitude - SunCalc-Höhe (Radiant)
 * @param {number|null} northAngle
 * @param {number} distance - Abstand der Lichtquelle (m), z. B. aus Garten-Bounds
 * @returns {[number, number, number]}
 */
export function getSunLightPosition(azimuth, altitude, northAngle, distance = 100) {
  const dir = getSunDirection3D(azimuth, altitude, northAngle);
  return [dir.x * distance, dir.y * distance, dir.z * distance];
}

/**
 * Sonnenauf- und -untergang für einen Ort und Tag — Slider-Grenzen der
 * Zeitregler. Bei Polartag/-nacht kann SunCalc `Invalid Date` liefern; das
 * reicht der Aufrufer als null durch und fällt auf einen Standardbereich zurück.
 *
 * @param {number} lat
 * @param {number} lon
 * @param {Date} date
 * @returns {{ sunrise: Date|null, sunset: Date|null }}
 */
export function getDaylightRange(lat, lon, date) {
  const times = SunCalc.getTimes(date, lat, lon);
  const valid = (d) => (d instanceof Date && !Number.isNaN(d.getTime()) ? d : null);
  return { sunrise: valid(times.sunrise), sunset: valid(times.sunset) };
}
